import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'next-i18next'
import { ReactElement, useState } from 'react'
import { match } from 'ts-pattern'
import Bike from 'assets/icons/event/bike.svg'
import Elevation from 'assets/icons/event/elevation.svg'
import Person from 'assets/icons/event/person.svg'
import Pool from 'assets/icons/event/pool.svg'
import Run from 'assets/icons/event/run.svg'
import Walk from 'assets/icons/event/walk.svg'
import { getMinQuantity } from 'components/Checkout/CartForm/utils'
import { getEventTypeStyle } from 'components/Hook/useEventCard'
import WaitingListModale from 'components/WaitingList/WaitingListModale'
import { Button, ButtonVariant } from 'components/common/Button'
import { Status } from 'components/common/Status/Status'
import { NewFlexContainer } from 'components/common/common.style'
import RacePageBadge from 'components/event/Badge/RacePageBadge'
import { FrontRaceService } from 'domain/services/RaceService/FrontRaceService'
import {
  DistanceOrElevationTranslationKey,
  DistanceOrElevationUnit,
  raceCategoriesLevel1
} from 'helpers/raceCategoryLevels'
import { usePriceFormat } from 'hooks/usePriceFormat'
import { LiveEventRace } from 'services/api/database/EventService'
import { unifiedColors } from 'styles/colors'
import {
  AlertBadgeContainer,
  ContentRaceFooter,
  CountCourse,
  CtaContainer,
  FlexContainer,
  MultiCourse,
  PriceValue,
  RaceCardContainer,
  RaceCardFooter,
  RaceData,
  RaceDetail,
  StepNumber,
  Type
} from './RaceCard.style'
import RaceCardTime from './RaceCardTime'
import { WaitingListFooter } from './WaitingListFooter'

interface FullRaceCardContentProps {
  race: LiveEventRace
  raceStatus: Status
  isValidPrice: boolean
  isWaitingList: boolean
  hasReduction: boolean
  finalPrice: { amount: number; currency: string }
  finalReductionPrice: { amount: number; currency: string }
  ctaLink: string
  handleClick: () => void
  ctaLabel: string
  isFeatured: boolean | null
  showRaceStock: boolean
}

export const FullRaceCardContent = ({
  race,
  raceStatus,
  isValidPrice,
  isWaitingList,
  hasReduction,
  finalPrice,
  finalReductionPrice,
  ctaLink,
  handleClick,
  ctaLabel,
  isFeatured,
  showRaceStock
}: FullRaceCardContentProps) => {
  const { t } = useTranslation(['common', 'event'])
  const [openWaitingListModale, setOpenWaitingListModale] = useState(false)

  const {
    name,
    categoryLevel1: type,
    categoryLevel2: distance,
    price,
    raceData,
    startDate,
    timeZone,
    raceInventory
  } = race

  const { formatPriceDefault } = usePriceFormat()
  let stepNumber = 0

  const remainingStock = raceInventory
    ? raceInventory.stockedQuantity - raceInventory.reserved
    : null

  const relevantLabels: DistanceOrElevationTranslationKey[] = [
    DistanceOrElevationTranslationKey.running,
    DistanceOrElevationTranslationKey.swimming,
    DistanceOrElevationTranslationKey.walking,
    DistanceOrElevationTranslationKey.biking
  ]
  const relevantCount =
    raceData?.reduce(
      (acc, elem) =>
        relevantLabels.includes(elem.translationKey) ? acc + 1 : acc,
      0
    ) ?? 0

  const { relevant, nonRelevant } = (raceData || []).reduce(
    (acc, elem) => {
      const {
        elevationSign,
        icon,
        isRelevant
      }: {
        icon: ReactElement | null
        isRelevant?: boolean
        elevationSign?: string
      } = match(elem.translationKey)
        .with(DistanceOrElevationTranslationKey.running, () => ({
          icon: <Run />,
          isRelevant: true
        }))
        .with(DistanceOrElevationTranslationKey.swimming, () => ({
          icon: <Pool />,
          isRelevant: true
        }))
        .with(DistanceOrElevationTranslationKey.walking, () => ({
          icon: <Walk />,
          isRelevant: true
        }))
        .with(DistanceOrElevationTranslationKey.biking, () => ({
          icon: <Bike />,
          isRelevant: true
        }))
        .with(DistanceOrElevationTranslationKey.age, () => ({
          icon: <Person />
        }))
        .with(DistanceOrElevationTranslationKey.positiveElevation, () => ({
          icon: <Elevation />,
          elevationSign: '+'
        }))
        .with(DistanceOrElevationTranslationKey.negativeElevation, () => ({
          icon: <Elevation style={{ transform: 'scale(-1, 1)' }} />,
          elevationSign: '-'
        }))
        .otherwise(() => ({
          icon: null
        }))

      if (isRelevant && relevantCount > 1) {
        stepNumber++
      }

      const course =
        elem.unit === DistanceOrElevationUnit.years ||
        (typeof elem.value === 'number' && elem.value > 0) ? (
          <CountCourse key={elem.key}>
            {isRelevant && relevantCount > 1 && (
              <StepNumber $total={relevantCount} $stepNumber={stepNumber}>
                {stepNumber}
              </StepNumber>
            )}
            <RaceDetail>
              {icon}
              {isRelevant && relevantCount > 1 && (
                <Typography
                  variant="body1"
                  paddingRight={0.8}
                  color={unifiedColors.label.default}
                >
                  {t(
                    `event:${
                      DistanceOrElevationTranslationKey[elem.translationKey]
                    }` as never
                  )}
                </Typography>
              )}
              <Typography variant="body1" color={unifiedColors.label.subdued}>
                {elevationSign !== '' && <span>{elevationSign}</span>}
                {elem.value}{' '}
                {elem.unit === DistanceOrElevationUnit.years
                  ? t('years')
                  : elem.unit}
              </Typography>
            </RaceDetail>
          </CountCourse>
        ) : null

      if (isRelevant && relevantCount > 1) {
        acc.relevant.push(course)
      } else {
        acc.nonRelevant.push(course)
      }

      return acc
    },
    {
      relevant: [] as Array<ReactElement | null>,
      nonRelevant: [] as Array<ReactElement | null>
    }
  )

  const displayAlertBadge =
    [Status.OUT_OF_STOCK, Status.WAITING_LIST].includes(raceStatus) ||
    showRaceStock

  const eventType = type
    ? getEventTypeStyle(type as keyof typeof raceCategoriesLevel1)
    : null

  return (
    <>
      <RaceCardContainer
        $noMarginBottom={isWaitingList}
        $noMarginTop={!displayAlertBadge}
      >
        <div>
          <Typography variant="h6" lineHeight={1.2}>
            {name}
          </Typography>
          <RaceData>
            {stepNumber > 0 && <MultiCourse>{relevant}</MultiCourse>}
            {nonRelevant}
            {!!startDate && (
              <RaceCardTime startDate={startDate} timeZone={timeZone} />
            )}
          </RaceData>
          {eventType && (
            <NewFlexContainer alignItems="center" gap="5px">
              <FlexContainer>
                <Type
                  $color={eventType.color}
                  $backgroundColor={eventType.backgroundColor}
                >
                  {t(eventType.value as never)}
                </Type>
              </FlexContainer>
              {distance && (
                <FlexContainer>
                  <Type $color="onBlue" $backgroundColor="blue">
                    {t(distance as never)}
                  </Type>
                </FlexContainer>
              )}
            </NewFlexContainer>
          )}
        </div>
        <RaceCardFooter $isValidPrice={isValidPrice} $largerMargin={!startDate}>
          <ContentRaceFooter>
            {isValidPrice ? (
              <Stack
                justifyContent="center"
                alignItems={{ xs: 'flex-start', md: 'flex-end' }}
              >
                <PriceValue $hasReduction={hasReduction} variant="body2">
                  {price === 0
                    ? t('free')
                    : formatPriceDefault(
                        finalPrice.amount,
                        finalPrice.currency,
                        getMinQuantity(race)
                      )}
                </PriceValue>
                {hasReduction && (
                  <PriceValue variant="body2" $isRed>
                    {finalReductionPrice.amount === 0
                      ? t('free')
                      : formatPriceDefault(
                          finalReductionPrice.amount,
                          finalReductionPrice.currency,
                          getMinQuantity(race)
                        )}
                  </PriceValue>
                )}
                {FrontRaceService.displayPerParticipantLabel(race) && (
                  <Typography variant="bodyMRegular" color="label.subdued">
                    {t('event:perParticipant')}
                  </Typography>
                )}
              </Stack>
            ) : null}
            {ctaLink && (
              <CtaContainer>
                {displayAlertBadge && (
                  <AlertBadgeContainer>
                    <RacePageBadge
                      remainingStock={showRaceStock ? remainingStock : 0}
                    />
                  </AlertBadgeContainer>
                )}
                <Button
                  variant={
                    !isFeatured
                      ? ButtonVariant.Default
                      : raceStatus !== Status.ONLINE
                      ? ButtonVariant.Disabled
                      : ButtonVariant.Primary
                  }
                  label={ctaLabel}
                  onClick={handleClick}
                />
              </CtaContainer>
            )}
          </ContentRaceFooter>
        </RaceCardFooter>
      </RaceCardContainer>
      {isWaitingList && (
        <WaitingListFooter
          openWaitingListModale={() => setOpenWaitingListModale(true)}
        />
      )}
      <WaitingListModale
        open={openWaitingListModale}
        onClose={() => setOpenWaitingListModale(false)}
        race={race}
      />
    </>
  )
}
